import React, { useState, useContext, useEffect, useRef } from 'react';
import { FormSchemaFields, FormContext, FormSchema } from '@sw-sw/lib-form';
import { UIControlType } from '@sw-sw/lib-form-control-types';
import { ActionButtons } from '../Shared/ActionButtons';
import { submitHandler } from '../Shared/form';
import ReactTooltip from 'react-tooltip';
import userApi from '../../utils/api/user';
import { toast } from 'react-toastify';
import { IconButton } from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';

const getSchema = (
  isEnabled: boolean,
  isSameForBoth: boolean,
  selectedcertificationType: any,
  applyToAll: boolean = false,
) => {
  ReactTooltip.rebuild();
  const schema: FormSchema = {
    certification_type: {
      label: 'Certification Type',
      controlType: UIControlType.select,
      labelKey: 'label',
      valueKey: 'value',
      options: [
        { value: 'compliance', label: 'Compliance' },
        { value: 'acknowledgement', label: 'Acknowledgement' },
        { value: 'both', label: 'Both' },
      ],
      validation: {
        required: true,
      },
      style: {
        flexBasis: '33%',
      },
    },
    no_of_days_compliance: {
      label: 'Due In (Compliance)',
      controlType: UIControlType.number,
      toolTipOptions: {
        message:
          'Set the number of days after an inspection is submitted by which this certification must be signed',
      },
      style: {
        flexBasis: '33%',
      },
      validation: {
        required: false,
      },
      disabled: true,
    },
    no_of_days_acknowledgment: {
      label: 'Due In (Acknowledgement)',
      controlType: UIControlType.number,
      toolTipOptions: {
        message:
          'Set the number of days after an inspection is submitted by which this certification must be signed',
      },
      style: {
        flexBasis: '33%',
      },
      validation: {
        required: false,
      },
      disabled: true,
    },
    sameForBoth: {
      label: 'Same for both',
      controlType: UIControlType.checkbox,
      style: {
        flexBasis: '100%',
        alignSelf: 'center',
        display: 'none',
      },
    },
    apply_to_all: {
      label: 'Apply to all',
      controlType: UIControlType.checkbox,
      className: 'backdated-checkbox',
      toolTipOptions: {
        message:
          'When checked this feature will be turned on, with current setting, for all clients and projects. If left unchecked this feature will need to be turned on manually for all clients.',
      },
      style: {
        flexBasis: '100%',
        alignSelf: 'center',
      },
    },
  };

  if (['compliance', 'both'].includes(selectedcertificationType)) {
    if (schema.no_of_days_compliance.validation) {
      schema.no_of_days_compliance.validation.required = true;
    }
    schema.no_of_days_compliance.disabled = false;
  }

  if (['acknowledgement', 'both'].includes(selectedcertificationType)) {
    if (schema.no_of_days_acknowledgment.validation) {
      schema.no_of_days_acknowledgment.validation.required = true;
    }
    schema.no_of_days_acknowledgment.disabled = false;
  }

  if (applyToAll && isEnabled && selectedcertificationType === 'both') {
    schema.sameForBoth.style!.display = 'block';
  }

  if (isSameForBoth && selectedcertificationType === 'both') {
    schema.no_of_days_compliance.label = 'Due In';
    if (schema.no_of_days_compliance.toolTipOptions) {
      schema.no_of_days_compliance.toolTipOptions.message =
        'Set the number of days after an inspection is submitted by which both the certifications must be signed';
    }
    delete schema.no_of_days_acknowledgment;
  }

  if (!isEnabled) {
    delete schema.certification_type;
    delete schema.no_of_days_compliance;
    delete schema.no_of_days_acknowledgment;
    delete schema.apply_to_all;
  }

  if (!applyToAll) {
    delete schema.certification_type;
    delete schema.no_of_days_compliance;
    delete schema.no_of_days_acknowledgment;
  }

  return schema;
};

const InspectionCertificationsTimeline = () => {
  const formContext = useContext(FormContext);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isLocked, setIsLocked] = useState(false);

  const isUserAction = useRef(false);

  useEffect(() => {
    const userDataFunc = async () => {
      const fetchdata = await userApi.tenantInfoInspection();
      if (fetchdata.getInspectionCertTimeline?.is_locked) {
        isUserAction.current = false;
        setIsLocked(true);
      }
      formContext.set('inspectionVal', fetchdata.getInspectionCertTimeline);
    };
    userDataFunc();
  }, []);

  useEffect(() => {
    if (isUserAction.current) {
      isUserAction.current = false;
      onSave({
        is_inspection_certification_timeline_enabled:
          formContext.value.is_inspection_certification_timeline_enabled,
        certification_type: formContext.value.certification_type,
        no_of_days_compliance: formContext.value.no_of_days_compliance,
        no_of_days_acknowledgment: formContext.value.no_of_days_acknowledgment,
        sameForBoth: formContext.value.sameForBoth,
        apply_to_all: formContext.value.apply_to_all,
      });
    }
  }, [isLocked]);

  useEffect(() => {
    const saveData = async () => {
      if (
        !formContext.value.is_inspection_certification_timeline_enabled &&
        formContext.value.inspectionVal &&
        formContext.value.inspectionVal
          .is_inspection_certification_timeline_enabled
      ) {
        await onSave({
          is_inspection_certification_timeline_enabled: false,
          certification_type: '',
          no_of_days_compliance: 0,
          no_of_days_acknowledgment: 0,
          sameForBoth: false,
          apply_to_all: true,
        });
      }
    };

    saveData();
  }, [formContext.value.is_inspection_certification_timeline_enabled]);

  const buildPayload = (data: any) => {
    let payload = {
      is_inspection_certification_timeline_enabled:
        data.is_inspection_certification_timeline_enabled,
      certification_type: 'compliance',
      no_of_days_compliance: 0,
      no_of_days_acknowledgment: 0,
      is_locked: isLocked && data.apply_to_all,
      apply_to_all: data.apply_to_all,
    };

    if (
      !data.is_inspection_certification_timeline_enabled ||
      !data.apply_to_all
    )
      return payload;

    payload.certification_type = data.certification_type;
    switch (data.certification_type) {
      case 'both':
        payload.no_of_days_compliance = data.no_of_days_compliance;
        payload.no_of_days_acknowledgment = data.sameForBoth
          ? data.no_of_days_compliance
          : data.no_of_days_acknowledgment;
        break;
      case 'compliance':
        payload.no_of_days_compliance = data.no_of_days_compliance;
        break;
      case 'acknowledgement':
        payload.no_of_days_acknowledgment = data.no_of_days_acknowledgment;
        break;
    }

    return payload;
  };

  const onSave = async (data: any) => {
    const toastId = toast('Saving Certification Timeline Details');
    const requiredData = buildPayload(data);
    return await userApi.tenantUpdateInspection(requiredData).then(() => {
      toast.update(toastId, {
        render: 'Inspection Certification Timeline saved successfully!',
        type: 'success',
      });
      formContext.set('inspectionVal', {
        ...formContext.value.inspectionVal,
        ...requiredData,
      });
    });
  };

  if (formContext.value.inspectionVal === null) {
    formContext.set('inspectionVal', {
      is_inspection_certification_timeline_enabled: false,
      certification_type: '',
      no_of_days_compliance: 0,
      no_of_days_acknowledgment: 0,
      sameForBoth: false,
      is_locked: false,
      apply_to_all: false,
    });
  }

  if (formContext.value.inspectionVal)
    return (
      <div className='inspection-signature-requirement-wrapper'>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            cursor: 'pointer',
            marginBottom: '10px',
          }}
          onClick={() => setIsCollapsed(!isCollapsed)}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <h3>Inspection Certification Timeline</h3>
            <FormSchemaFields
              schema={{
                is_inspection_certification_timeline_enabled: {
                  controlType: UIControlType.toggleButton,
                  toggleSize: 'sm',
                  style: {
                    marginTop: '10px',
                    marginLeft: '40px',
                  },
                },
              }}
              formData={formContext.value}
              onChange={formContext.set}
              initialFormData={{
                is_inspection_certification_timeline_enabled:
                  formContext.value.inspectionVal
                    .is_inspection_certification_timeline_enabled,
              }}
            />
            {formContext.value.is_inspection_certification_timeline_enabled &&
              formContext.value.apply_to_all && (
                <IconButton
                  size='medium'
                  onClick={(e) => {
                    e.stopPropagation();
                    isUserAction.current = true;
                    setIsLocked(!isLocked);
                  }}
                  data-tip='Lock across all Divisions, Clients and Projects'
                  style={{
                    marginTop: '-5px',
                  }}
                >
                  {isLocked ? (
                    <LockIcon className='col-danger' />
                  ) : (
                    <LockOpenIcon className='col-success' />
                  )}
                </IconButton>
              )}
          </div>
          <i
            className={isCollapsed ? 'fa fa-chevron-down' : 'fa fa-chevron-up'}
            aria-hidden='true'
          />
        </div>
        <hr />
        <br />
        <div
          style={{
            display: isCollapsed ? 'none' : 'block',
          }}
        >
          <FormSchemaFields
            className='inspection-signature-requirement-form findings-form'
            schema={getSchema(
              formContext.value.is_inspection_certification_timeline_enabled,
              formContext.value.sameForBoth,
              formContext.value.certification_type || '',
              formContext.value.apply_to_all || false,
            )}
            onChange={formContext.set}
            formData={formContext.value}
            initialFormData={{
              certification_type:
                formContext.value.inspectionVal.certification_type || '',
              no_of_days_compliance:
                formContext.value.inspectionVal.no_of_days_compliance || 0,
              no_of_days_acknowledgment:
                formContext.value.inspectionVal.no_of_days_acknowledgment || 0,
              sameForBoth:
                formContext.value.inspectionVal.no_of_days_compliance ===
                formContext.value.inspectionVal.no_of_days_acknowledgment,
              apply_to_all:
                formContext.value.inspectionVal.apply_to_all || false,
            }}
          />
          {!formContext.value.is_inspection_certification_timeline_enabled ? (
            <p className="col-danger">
              Note: Enable this to set no of days for compliance and/or
              acknowledgment
            </p>
          ) : (
            <ActionButtons
              primary='Save'
              onClick={(e: React.SyntheticEvent<Element, Event>) =>
                submitHandler(formContext, e, onSave)
              }
            />
          )}
        </div>
      </div>
    );
  else
    return (
      <div className='inspection-signature-requirement-wrapper'>
        <h3>Inspection Certification Timeline</h3>
        <p>Loading...</p>
      </div>
    );
};

export default InspectionCertificationsTimeline;
